import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { QuestionnaireResponse } from "@medplum/fhirtypes";

export interface EncounterChildSaveOptions {
  status: QuestionnaireResponse["status"];
}

/**
 * Whether this save is the encounter being finalized rather than a plain Save.
 *
 * The encounter drives every section through one status: `in-progress` is Save, `completed` is
 * Sign & Lock, and `amended` is an amendment's Sign & Lock. A section that only acts once the
 * encounter is signed reads that here, so the convention lives with the option it describes.
 */
export function isEncounterSignAndLock({ status }: EncounterChildSaveOptions): boolean {
  return status === "completed" || status === "amended";
}

type EncounterChildSaveHandler = (options: EncounterChildSaveOptions) => Promise<void>;

/**
 * When a handler runs relative to the others.
 *
 * `section` handlers own separate parts of the encounter and nothing about one depends on another, so
 * they run together. `finalize` handlers run once every section has finished, which is what lets work
 * built out of what the save wrote read the encounter instead of racing the writes that produce it.
 */
export type EncounterChildSavePhase = "section" | "finalize";

interface RegisteredChildSave {
  handler: EncounterChildSaveHandler;
  phase: EncounterChildSavePhase;
}

export interface EncounterChildSaveCoordinator {
  register: (
    key: symbol,
    handler: EncounterChildSaveHandler,
    phase?: EncounterChildSavePhase,
  ) => () => void;
  saveAll: (options: EncounterChildSaveOptions) => Promise<void>;
}

const EncounterChildSaveContext = createContext<EncounterChildSaveCoordinator | null>(null);

export function useEncounterChildSaveCoordinator(): EncounterChildSaveCoordinator {
  const handlersRef = useRef(new Map<symbol, RegisteredChildSave>());

  const register = useCallback(
    (
      key: symbol,
      handler: EncounterChildSaveHandler,
      phase: EncounterChildSavePhase = "section",
    ) => {
      handlersRef.current.set(key, { handler, phase });
      return () => handlersRef.current.delete(key);
    },
    [],
  );

  // Sections first, then finalizers. A section that rejects stops the finalizers, so nothing runs
  // against an encounter whose sections did not finish writing.
  const saveAll = useCallback(async (options: EncounterChildSaveOptions) => {
    const registered = Array.from(handlersRef.current.values());
    const runPhase = (phase: EncounterChildSavePhase) =>
      Promise.all(
        registered.filter((entry) => entry.phase === phase).map(({ handler }) => handler(options)),
      );

    await runPhase("section");
    await runPhase("finalize");
  }, []);

  return useMemo(() => ({ register, saveAll }), [register, saveAll]);
}

export function EncounterChildSaveProvider({
  coordinator,
  children,
}: {
  coordinator: EncounterChildSaveCoordinator;
  children: ReactNode;
}) {
  return (
    <EncounterChildSaveContext.Provider value={coordinator}>
      {children}
    </EncounterChildSaveContext.Provider>
  );
}

// Registers against the given coordinator, keeping the latest render's handler so it always reads
// current state without the caller having to memoize it.
function useRegisterChildSave(
  coordinator: EncounterChildSaveCoordinator | null,
  handler: EncounterChildSaveHandler,
  phase: EncounterChildSavePhase,
  enabled: boolean,
) {
  const keyRef = useRef(Symbol("encounter-child-save"));
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!coordinator || !enabled) return;
    return coordinator.register(keyRef.current, (options) => handlerRef.current(options), phase);
  }, [coordinator, enabled, phase]);
}

/**
 * Whether the shell around this section runs a child save at all. A section that can only write
 * through that handler reads this and hides the controls whose work would never land.
 */
export function useHasEncounterChildSave(): boolean {
  return Boolean(useContext(EncounterChildSaveContext));
}

/**
 * The encounter's child-save coordinator, or null when this tree is not inside a provider.
 */
export function useEncounterChildSave(): EncounterChildSaveCoordinator | null {
  return useContext(EncounterChildSaveContext);
}

/**
 * Registers a section of the encounter to be saved alongside the others.
 *
 * Takes the coordinator from context, because a section is rendered somewhere inside the encounter's
 * tree rather than by the encounter itself.
 */
export function useRegisterEncounterChildSave(handler: EncounterChildSaveHandler, enabled = true) {
  const coordinator = useContext(EncounterChildSaveContext);

  useRegisterChildSave(coordinator, handler, "section", enabled);
}

/**
 * Registers work that runs once every section of the encounter has saved.
 *
 * Takes the coordinator directly rather than from context: a finalizer belongs to the encounter that
 * owns the save, which holds the coordinator and sits above the provider. Registering it there is
 * also what keeps it from depending on any particular part of the encounter's UI being mounted.
 */
export function useRegisterEncounterFinalize(
  coordinator: EncounterChildSaveCoordinator,
  handler: EncounterChildSaveHandler,
  enabled = true,
) {
  useRegisterChildSave(coordinator, handler, "finalize", enabled);
}
